import { useCallback, useEffect, useRef, useState } from 'react';

import { AppStateStatus, Platform } from 'react-native';

import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';

import { LANGUAGE_KEYS } from 'config/languageKeys';

import { setMuted, setVolume } from 'store/player/actions';
import { StreamTypes } from 'store/player/actions.types';
import {
    selectPlayerIsMuted,
    selectPlayerVolume,
    selectSubtitlesFontSize,
} from 'store/player/selectors';
import { selectChannelsById } from 'store/programs/selectors.ts';
import { RBChannel } from 'store/railsBuilder/reducer.types';
import { selectVideoSessionId } from 'store/session/selectors';
import { getEntitlements } from 'store/user/actions.ts';
import { selectIfKidsProfileActive } from 'store/user/selectors.ts';

import { VideoItem } from 'services/data-manager/types';
import { SSM_CONENTENT_TOKEN_ERROR_403 } from 'services/ssm/constants.ts';
import useHandlePlaybackStartStopWatchEvents from 'services/uav/useHandlePlaybackStartStopWatchEvents';

import { useCast } from 'utils/cast/useCast';
import { ERROR_CODE_P008 } from 'utils/error/errorCodes';
import useBookmarks from 'utils/hooks/useBookmarks';
import isSubscribedToChannel from 'utils/isSubscribedToChannel.ts';
import { isEmpty } from 'utils/object';
import { isTablet, isWeb } from 'utils/responsiveness';
import { switchImmersiveModeOn } from 'utils/systemUI';

import useAirPlay from 'screens/PlayerModal/hooks/useAirPlay.ts';

import { BitmovinPlayerError, PlayerRef } from 'components/Player/types.ts';
import useBingeWatching from 'components/PlayerOverlay/hooks/useBingeWatching.ts';
import { StandardUavProps } from 'components/Ribbon/types';

import {
    PlaybackStopParams,
    PlayerError,
    StartoverStreamType,
    SwitchPlayerSource,
} from '../types';
import { STARTOVER_STREAM_TYPE } from '../utils';
import {
    CDN_PLAYER_ERRORS_MAP,
    getErrorDescription,
    isConcurrencyError,
} from '../utils/errors.ts';
import useAudioAndSubtitles from './useAudioAndSubtitles';
import useChannelsZapping from './useChannelsZapping';
import useConnectionTypeChange from './useConnectionTypeChange';
import useDisabledNotifications from './useDisabledNotifications';
import useFrameFitting from './useFrameFitting';
import useInHomeDetectionBlockStream from './useInHomeDetectionBlockStream';
import useInHomeDetectionErrorHandler from './useInHomeDetectionErrorHandler';
import useJumpToLive from './useJumpToLive';
import useLivePosition from './useLivePosition';
import useLiveProgramFinished from './useLiveProgramFinished.ts';
import useOnStatisticsUpdate from './useOnStatisticsUpdate';
import usePlayerBlackoutInterval from './usePlayerBlackoutInterval.ts';
import usePlayerError from './usePlayerError';
import usePlayerPlayPause from './usePlayerPlayPause.ts';
import usePlayerProgress from './usePlayerProgress';
import usePlayerProgressBar from './usePlayerProgressBar.ts';
import usePlayerReady from './usePlayerReady.ts';
import useSkipBackward from './useSkipBackward.ts';
import useSkipForward from './useSkipForward.ts';
import useSkippings from './useSkippings';
import useStartover from './useStartover';
import useStreamPersistence from './useStreamPersistence.ts';
import useSubscribeToEntitlementsChange from './useSubscribeToEntitlementsChange';
import useTimeshift from './useTimeshift';
import useTimeshiftAutoplay from './useTimeshiftAutoplay';
import useTimeshiftNotifications from './useTimeshiftNotifications';
import useTrickModeControl from './useTrickModeControl';
import useVideoSource from './useVideoSource';

type Params = {
    appState: AppStateStatus;
    channel: RBChannel | undefined;
    videoItem: VideoItem | undefined;
    playbackType: StreamTypes | undefined;
    visible: boolean;
    fullscreen: boolean;
    embedded?: boolean;
    onSwitchPlayerSource: SwitchPlayerSource;
    handleRestoreLiveStream(): void;
    profileId?: string | undefined;
    recordingId?: string | undefined;
    shouldWarnOnMobileData?: boolean;
    startoverStreamType?: StartoverStreamType;
    subtitlesDialogVisible?: boolean;
    recordingActionsDialogVisible?: boolean;
} & StandardUavProps;

const {
    UNSUBSCRIBED_PLAYER_INFO_BODY,
    UNSUBSCRIBED_PLAYER_INFO_TITLE,
    PLAYER_ERROR_CONCURRENCY_ERROR,
} = LANGUAGE_KEYS;

function usePlayerSharedBehaviour({
    channel,
    videoItem,
    playbackType,
    visible,
    fullscreen,
    embedded = false,
    railId,
    templateId,
    appState,
    onSwitchPlayerSource,
    profileId,
    recordingId,
    shouldWarnOnMobileData,
    handleRestoreLiveStream,
    subtitlesDialogVisible,
    recordingActionsDialogVisible,
    startoverStreamType = STARTOVER_STREAM_TYPE,
}: Params) {
    const { t } = useTranslation();
    const dispatch = useDispatch();

    // TODO: Disabled due to REAC-2172 issue(s) related to DRM configuration.
    // const isAirPlayConnected = useAirplayConnectivity();
    const isAirPlayConnected = false;

    const playerRef = useRef<PlayerRef | null>(null);

    const genericVideoSessionId = useSelector(selectVideoSessionId);
    const muted = useSelector(selectPlayerIsMuted);
    const volume = useSelector(selectPlayerVolume);
    const kidsProfileActive = useSelector(selectIfKidsProfileActive);
    const subtitlesFontSize = useSelector(selectSubtitlesFontSize);
    const channelsById = useSelector(selectChannelsById);

    const isLive = playbackType === StreamTypes.live;
    const isStartover = playbackType === StreamTypes.startover;
    const isCatchup = playbackType === StreamTypes.catchup;
    const isNpvr = playbackType === StreamTypes.npvr;
    const isVod = playbackType === StreamTypes.vod;

    const [playerWaiting, setPlayerWaiting] = useState(false);
    const [playerPaused, setPlayerPaused] = useState(false);
    const [playerPausedByUser, setPlayerPausedByUser] = useState(false);
    const [videoDuration, setVideoDuration] = useState(0);
    const [sliderIsActive, setSliderIsActive] = useState(false);

    const { playerProgress, setPlayerProgress, onPlayerProgress } =
        usePlayerProgress({
            playerRef,
            videoItem,
            isCatchup,
            isStartover,
        });

    const { livePosition, onResetLivePosition } = useLivePosition({
        playerRef,
        playerProgress,
        isLive,
        isCatchup,
        isStartover,
        playerPaused,
        videoItem,
        sliderIsActive,
    });

    useLiveProgramFinished({
        isLive,
        channel,
        videoItem,
        playerProgress,
        onSwitchPlayerSource,
        onSetVideoDuration: setVideoDuration,
    });

    const {
        isSOTSPauseDisabled,
        disabledNotification,
        onResetDisabledNotification,
        onShowTimeshiftNotAvailableNotification,
        onShowControlsRestrictedNotification,
    } = useDisabledNotifications({ isLive, livePosition });

    const { outOfHomeBlockStream } = useInHomeDetectionBlockStream(
        channel,
        videoItem,
        playbackType,
    );

    const { readyToPlay, setReadyToPlay, onPlayerReady } = usePlayerReady({
        playerRef,
        videoItem,
        channel,
        playerPaused,
        profileId,
        isCatchup,
        isStartover,
        setVideoDuration,
        setPlayerProgress,
    });

    const { playerError, onSetPlayerError, onResetPlayerError } =
        usePlayerError({
            videoItem,
            playbackType,
            videoSessionId: genericVideoSessionId,
            onSetReadyToPlay: setReadyToPlay,
        });

    const { onVideoLoaded } = useHandlePlaybackStartStopWatchEvents({
        playbackType,
        railId,
        templateId,
        videoItem,
        playerError,
        playerProgress,
        videoDuration,
    });

    const playerReady =
        readyToPlay && !outOfHomeBlockStream && !shouldWarnOnMobileData;

    const {
        seeking,
        setSeeking,
        handleSkip,
        handleSeeked,
        handleResetSeeking,
    } = useSkippings({
        playerRef,
        videoDuration,
        playerPausedByUser,
        isCatchup,
        isStartover,
        livePosition,
    });

    const { onUpdateBookmark } = useBookmarks({
        genericVideoSessionId,
        videoDuration,
        playerReady,
        videoItem,
        playbackType,
        playerProgress,
        recordingId,
        sliderIsActive,
    });

    const handleSetUnsubscribedChannelError = useCallback(
        (videoSessionId?: string) => {
            const error: PlayerError = {
                message: t(UNSUBSCRIBED_PLAYER_INFO_TITLE),
                description: t(UNSUBSCRIBED_PLAYER_INFO_BODY),
                code: ERROR_CODE_P008,
            };

            onSetPlayerError(error, videoSessionId);
        },
        [onSetPlayerError, t],
    );

    const {
        videoSource,
        onStopPlaybackSession,
        onResetVideoSource,
        showPinDialog,
        onParentalControlChecked,
        onParentalCheckDismiss,
        onResetVideoItem,
        showContentRatingOverlay,
    } = useVideoSource({
        embedded,
        fullscreen,
        visible,
        outOfHomeBlockStream,
        channel,
        originalVideoItem: videoItem,
        originalPlaybackType: playbackType,
        onSetPlayerError,
        onUnsubscribedChannelError: handleSetUnsubscribedChannelError,
        onResetPlayerError,
        shouldWarnOnMobileData,
    });

    const handleStopPlayer = useCallback(async () => {
        onUpdateBookmark();
        setReadyToPlay(false);
        onStopPlaybackSession();
    }, [onUpdateBookmark, onStopPlaybackSession, setReadyToPlay]);

    const handleSwitchPlayerSource = useCallback<SwitchPlayerSource>(
        async (_videoItem, _playbackType) => {
            if (playbackType !== _playbackType) {
                await handleStopPlayer();
            }

            onSwitchPlayerSource(_videoItem, _playbackType);
        },
        [onSwitchPlayerSource, handleStopPlayer, playbackType],
    );

    const errorExists = !isEmpty(playerError);
    const prevErrorExists = useRef(errorExists);

    const { onStatisticsUpdate } = useOnStatisticsUpdate({
        playerProgress,
        videoItem,
        playbackType,
    });

    useSubscribeToEntitlementsChange({
        videoSource,
        playerError,
        playbackType,
        channel,
        onSetUnsubsribedChannelError: handleSetUnsubscribedChannelError,
    });

    usePlayerBlackoutInterval({
        playerError,
        videoItem,
        onSwitchPlayerSource: handleSwitchPlayerSource,
    });

    useInHomeDetectionErrorHandler({
        outOfHomeBlockStream,
        playerVisible: visible,
        playerError,
        onBlock: useCallback(
            (description: string) => {
                onSetPlayerError({
                    description,
                });
            },
            [onSetPlayerError],
        ),
        videoItem,
        onUnblock: onResetPlayerError,
        isPinDialogVisible: showPinDialog,
        embedded,
        fullscreen,
        shouldWarnOnMobileData,
    });

    const {
        timeshiftAllowed,
        timeshiftModeOn,
        timeshiftPosition,
        timeshiftDuration,
        timeshiftIndicatorEnabled,
        onSwitchTimeshiftModeOn,
        onSwitchTimeshiftModeOff,
        onPauseLiveStream,
    } = useTimeshift({
        videoItem,
        startoverStreamType,
        onSwitchPlayerSource: handleSwitchPlayerSource,
    });

    const {
        timeshiftNotification,
        onSetTimeshiftNotification,
        onResetTimeshiftNotification,
        onResetTimeshiftNotificationShown,
    } = useTimeshiftNotifications();

    const { startoverAllowed, startoverButtonVisible, onPressStartover } =
        useStartover({
            playerRef,
            videoItem,
            playerReady,
            isLive,
            isCatchup,
            isStartover,
            isVod,
            handleSkip,
            onSwitchPlayerSource: handleSwitchPlayerSource,
            isSoDisabled: isSOTSPauseDisabled,
            showSoDisabledNotification: onShowTimeshiftNotAvailableNotification,
        });

    const { rewindAccessLevel, pauseAccessLevel, fastforwardAccessLevel } =
        useTrickModeControl({
            videoItem,
            playbackType,
            timeshiftModeOn,
            timeshiftAllowed,
            startoverAllowed,
        });

    const onPlayPausePress = usePlayerPlayPause({
        playerRef,
        videoItem,
        playerPaused,
        setPlayerPausedByUser,
        setPlayerPaused,
        setReadyToPlay,
        pauseAccessLevel,
        isLive,
        isSOTSPauseDisabled,
        playerProgress,
        timeshiftAllowed,
        startoverAllowed,
        templateId,
        railId,
        onShowControlsRestrictedNotification,
        onShowTimeshiftNotAvailableNotification,
        onSetTimeshiftNotification,
        onSwitchTimeshiftModeOn,
        onPauseLiveStream,
        onUpdateBookmark,
    });

    const handleJumpToLive = useJumpToLive({
        channel,
        videoItem,
        onSwitchPlayerSource,
        onResetTimeshiftNotification,
        onResetTimeshiftNotificationShown,
        onSwitchTimeshiftModeOff,
        onUpdateBookmark,
        setPlayerPaused,
        setPlayerPausedByUser,
        onSetReadyToPlay: setReadyToPlay,
    });

    const handleSkipBackward = useSkipBackward({
        playerRef,
        videoItem,
        playbackType,
        isLive,
        isCatchup,
        isStartover,
        isSOTSPauseDisabled,
        timeshiftAllowed,
        startoverAllowed,
        rewindAccessLevel,
        playerProgress,
        seeking,
        startoverStreamType,
        playerPausedByUser,
        setPlayerProgress,
        setReadyToPlay,
        handleSkip,
        onShowControlsRestrictedNotification,
        onShowTimeshiftNotAvailableNotification,
        onSwitchTimeshiftModeOn,
        onSetTimeshiftNotification,
        handleSwitchPlayerSource,
    });

    const handleSkipForward = useSkipForward({
        playerRef,
        videoItem,
        isLive,
        isCatchup,
        isStartover,
        fastforwardAccessLevel,
        videoDuration,
        playerProgress,
        seeking,
        playerPausedByUser,
        templateId,
        railId,
        setPlayerProgress,
        handleSkip,
        onShowControlsRestrictedNotification,
        handleJumpToLive,
    });

    const { wasCastSessionEnded, setWasCastSessionEnded } = useCast();

    const skipBackwardDisabled = isLive && !startoverAllowed;
    const skipForwardDisabled = isLive || isSOTSPauseDisabled;

    const {
        playerTracks,
        selectedAudio,
        selectedSubtitles,
        onTracksChanged,
        onSelectAudio,
        onSelectSubtitles,
    } = useAudioAndSubtitles({
        playerRef,
        visible,
        readyToPlay,
        channelId: channel?.editorial.id,
        streamType: playbackType,
    });

    const handlePlaying = useCallback(() => {
        handleResetSeeking();

        if (!playerPausedByUser) {
            setPlayerPaused(false);
        }

        setPlayerWaiting(false);
    }, [handleResetSeeking, playerPausedByUser]);

    const handleWaiting = useCallback(() => {
        if (!playerPaused) {
            setPlayerWaiting(true);
        }
    }, [playerPaused]);

    const zapping = useChannelsZapping({
        visible,
        videoItem,
        playbackType,
        embedded,
        fullscreen,
        subtitlesDialogVisible,
        recordingActionsDialogVisible,
        setPlayerPaused,
        setPlayerPausedByUser,
        onSwitchPlayerSource,
        onResetTimeshiftNotification,
        onResetTimeshiftNotificationShown,
        onSwitchTimeshiftModeOff,
        onUpdateBookmark,
        onSetReadyToPlay: setReadyToPlay,
    });

    const frameFitting = useFrameFitting({
        visible,
        fullscreen,
        errorExists,
        embedded,
    });

    const handleStopOnLivePlaybackError = useCallback(async () => {
        await handleStopPlayer();
        onResetVideoItem();
        setPlayerPausedByUser(false);
        setPlayerPaused(false);
        onResetLivePosition();
        onResetVideoSource();
        onSwitchTimeshiftModeOff();
        onResetTimeshiftNotificationShown();
    }, [
        handleStopPlayer,
        onResetVideoItem,
        onResetLivePosition,
        onResetVideoSource,
        onSwitchTimeshiftModeOff,
        onResetTimeshiftNotificationShown,
    ]);

    const handleConcurrencyError = useCallback(
        (error: BitmovinPlayerError) => {
            if (Platform.OS === 'android' && wasCastSessionEnded) {
                handleStopOnLivePlaybackError();
                onSwitchPlayerSource(videoItem!, StreamTypes.live);
                setWasCastSessionEnded(false);
            } else {
                onSetPlayerError({
                    code: error.code,
                    description: t(PLAYER_ERROR_CONCURRENCY_ERROR),
                });
            }
        },
        [
            wasCastSessionEnded,
            handleStopOnLivePlaybackError,
            onSwitchPlayerSource,
            videoItem,
            setWasCastSessionEnded,
            onSetPlayerError,
            t,
        ],
    );

    const handleUnsubscribedChannelError = useCallback(
        (error: BitmovinPlayerError) => {
            dispatch(
                getEntitlements({
                    autoRetry: false,
                    onSuccess(_entitlements) {
                        if (_entitlements && videoItem?.channelId) {
                            const channelSource =
                                channelsById[videoItem.channelId];
                            const notSubscribed =
                                playbackType !== StreamTypes.vod &&
                                (!channelSource ||
                                    !isSubscribedToChannel(
                                        channelSource,
                                        _entitlements,
                                    ));

                            if (notSubscribed) {
                                handleSetUnsubscribedChannelError(
                                    genericVideoSessionId,
                                );
                            } else {
                                onSetPlayerError({
                                    code: error.code,
                                    description: t(
                                        getErrorDescription(error.code),
                                    ),
                                });
                            }
                        } else {
                            onSetPlayerError({
                                code: error.code,
                                description: t(getErrorDescription(error.code)),
                            });
                        }
                    },
                    onFailure() {
                        onSetPlayerError({
                            code: error.code,
                            description: t(getErrorDescription(error.code)),
                        });
                    },
                }),
            );
        },
        [
            channelsById,
            dispatch,
            genericVideoSessionId,
            handleSetUnsubscribedChannelError,
            onSetPlayerError,
            playbackType,
            t,
            videoItem?.channelId,
        ],
    );

    const handlePlayerError = useCallback(
        (error: BitmovinPlayerError) => {
            if (isConcurrencyError(error)) {
                handleConcurrencyError(error);
            } else if (error.code === SSM_CONENTENT_TOKEN_ERROR_403) {
                handleUnsubscribedChannelError(error);
            } else if (CDN_PLAYER_ERRORS_MAP[error.data?.statusCode]) {
                const cdnError = CDN_PLAYER_ERRORS_MAP[error.data?.statusCode];

                onSetPlayerError({
                    code: cdnError.code,
                    message: cdnError.message ? t(cdnError.message) : undefined,
                    description: cdnError.description
                        ? t(cdnError.description)
                        : undefined,
                });
            } else {
                onSetPlayerError({
                    code: error.code,
                    description: t(getErrorDescription(error.code)),
                });
            }
        },
        [
            handleConcurrencyError,
            dispatch,
            videoItem?.channelId,
            channelsById,
            playbackType,
            handleSetUnsubscribedChannelError,
            genericVideoSessionId,
            onSetPlayerError,
            t,
        ],
    );

    const handleStartover = useCallback(() => {
        setPlayerPausedByUser(false);
        onSwitchTimeshiftModeOff();
        onPressStartover();
    }, [onPressStartover, onSwitchTimeshiftModeOff]);

    const handleStopPlayback = useCallback(
        (params: PlaybackStopParams = {}) => {
            const { resetSource, resetPauseState = true } = params;

            if (resetPauseState) {
                setPlayerPausedByUser(false);
                setPlayerPaused(false);
            }

            setSeeking(false);
            setReadyToPlay(false);
            setVideoDuration(0);
            setPlayerProgress(0);
            onResetPlayerError();
            onResetLivePosition();
            onResetVideoSource(resetSource);
            onResetVideoItem();
            onSwitchTimeshiftModeOff();
            onResetTimeshiftNotificationShown();
            onResetDisabledNotification();
        },
        [
            setSeeking,
            setReadyToPlay,
            setPlayerProgress,
            onResetPlayerError,
            onResetLivePosition,
            onResetVideoSource,
            onResetVideoItem,
            onSwitchTimeshiftModeOff,
            onResetTimeshiftNotificationShown,
            onResetDisabledNotification,
        ],
    );

    const handleResetPlayer = useCallback(() => {
        onUpdateBookmark(true);

        if (isVod) {
            handleStopPlayback({ resetSource: true });
        } else {
            handleStopPlayback({ resetSource: false });
        }
    }, [handleStopPlayback, isVod, onUpdateBookmark]);

    const {
        onSlidingValueChanged,
        onSlidingStart,
        onSlidingCompleted,
        onSlidingCompleteSilently,
    } = usePlayerProgressBar({
        playerRef,
        videoItem,
        isLive,
        isCatchup,
        isStartover,
        timeshiftAllowed,
        startoverAllowed,
        startoverStreamType,
        videoDuration,
        playerProgress,
        livePosition,
        playerPausedByUser,
        isSOTSPauseDisabled,
        rewindAccessLevel,
        fastforwardAccessLevel,
        skipBackwardDisabled,
        skipForwardDisabled,
        templateId,
        railId,
        setSliderIsActive,
        setReadyToPlay,
        setPlayerProgress,
        setSeeking,
        handleSwitchPlayerSource,
        handleSkip,
        onShowTimeshiftNotAvailableNotification,
        onSetTimeshiftNotification,
        handleJumpToLive,
        onShowControlsRestrictedNotification,
    });

    const handleSetMuted = useCallback((_muted?: boolean) => {
        dispatch(setMuted(_muted));
    }, []);

    const handleChangeVolume = useCallback((_volume: number) => {
        dispatch(setVolume(_volume));
    }, []);

    useAirPlay({
        playerRef,
        visible,
        isAirPlayConnected,
        playerPaused,
        onPlayPausePress,
    });

    useConnectionTypeChange({
        playerVisible: visible,
    });

    useStreamPersistence({
        isLive,
        isCatchup,
        isNpvr,
        isVod,
        isAirPlayConnected,
        appState,
        playerError,
        visible,
        readyToPlay,
        videoItem,
        playbackType,
        playerProgress,
        playerPaused,
        onPlayPausePress,
        handleStopPlayback,
        handleSwitchPlayerSource,
        handleRestoreLiveStream,
    });

    useTimeshiftAutoplay({
        playerProgress,
        playerPaused,
        livePosition,
        timeshiftModeOn,
        timeshiftDuration,
        onPlayerPausePress: onPlayPausePress,
    });

    useEffect(() => {
        if (errorExists && !prevErrorExists.current) {
            if (playbackType === StreamTypes.live) {
                handleStopOnLivePlaybackError();
            } else {
                playerRef.current?.pause();
                handleStopPlayer();
            }
        }

        prevErrorExists.current = errorExists;
    }, [errorExists]);

    useEffect(() => {
        if (embedded && playbackType === StreamTypes.live) {
            onResetTimeshiftNotification();
            onResetTimeshiftNotificationShown();
        }
    }, [
        embedded,
        playbackType,
        onResetTimeshiftNotification,
        onResetTimeshiftNotificationShown,
    ]);

    useEffect(() => {
        if (playerReady && !embedded && isTablet) {
            switchImmersiveModeOn();
        }
    }, [playerReady, embedded]);

    const {
        bingewatchingCountdown,
        isBingeWatchingVisible,
        nextEpisode,
        onPlayNextEpisode,
        onCloseBingeWatching,
        bingeTransitionInProgressRef,
    } = useBingeWatching({
        currentlyPlayingEpisode: videoItem,
        playerProgress,
        videoDuration,
        isPaused: playerPaused,
        isVod,
        isNpvr,
        isCatchup,
        playerReady,
        seeking: sliderIsActive,
        handleSwitchPlayerSource,
        handleResetPlayer,
    });

    return {
        onVideoLoaded,
        onPlayerReady,
        handleSeeked,
        onPlayerProgress,
        muted:
            muted ||
            !readyToPlay ||
            (playbackType === StreamTypes.live &&
                !isAirPlayConnected &&
                (showPinDialog ||
                    !visible ||
                    (!isWeb && appState === 'background'))),
        jumpToLiveAllowed:
            !kidsProfileActive && !isLive && !isVod && !errorExists,
        volume,
        subtitlesFontSize,
        playerRef,
        isLive,
        isStartover,
        isCatchup,
        isNpvr,
        isVod,
        videoSource,
        showPinDialog,
        errorExists,
        playerError,
        videoDuration,
        playerProgress,
        livePosition,
        playerReady,
        playerPaused,
        timeshiftModeOn,
        timeshiftPosition,
        timeshiftDuration,
        skipBackwardDisabled,
        skipForwardDisabled,
        startoverButtonVisible,
        startoverAllowed,
        timeshiftNotification,
        timeshiftIndicatorEnabled,
        disabledNotification,
        rewindAccessLevel,
        pauseAccessLevel,
        fastforwardAccessLevel,
        onResetDisabledNotification,
        seeking,
        playerTracks,
        selectedAudio,
        selectedSubtitles,
        isSOTSPauseDisabled,
        handleStopPlayer,
        onPlayPausePress,
        handlePlayerError,
        handlePlaying,
        handleSkipForward,
        handleSkipBackward,
        handleJumpToLive,
        handleStartover,
        onSlidingValueChanged,
        onSlidingStart,
        onSlidingCompleted,
        onSlidingCompleteSilently,
        onStatisticsUpdate,
        onParentalControlChecked,
        onParentalCheckDismiss,
        onResetTimeshiftNotification,
        onStopPlayback: handleStopPlayback,
        onTracksChanged,
        onSelectAudio,
        onSelectSubtitles,
        onResetPlayerError,
        showContentRatingOverlay,
        zapping,
        frameFitting,
        handleSetMuted,
        handleChangeVolume,
        playerWaiting,
        handleWaiting,
        bingewatchingCountdown,
        isBingeWatchingVisible,
        nextEpisode,
        onPlayNextEpisode,
        onCloseBingeWatching,
        bingeTransitionInProgressRef,
    };
}

export default usePlayerSharedBehaviour;
